import { skipToken, useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { useApi } from '#providers/api/index.ts';
import { useAuthentication } from '#providers/authentication/index.ts';
import { useProjects } from '#providers/projects/index.ts';
import { useProjectMutation } from '#providers/projects/use-project-mutation/index.ts';
import {
  getQueryLoadState,
  useInvalidateQueries,
} from '#providers/query/index.ts';
import { TicketsContext } from './Tickets.context.ts';
import * as helpers from './Tickets.helpers.ts';
import type {
  TTicketCreation,
  TTicketingOptionsRequest,
  TTicketUpdate,
  TTicketsContextValue,
  TTicketsProviderProps,
} from './Tickets.types.ts';

export const TicketsProvider = ({
  list,
  labels,
  displayId,
  children,
  onConversationEscalated,
}: TTicketsProviderProps) => {
  const api = useApi();
  const invalidateQueries = useInvalidateQueries();
  const { user } = useAuthentication();
  const { currentProjectId, isPrimaryUser, requireProjectId } = useProjects();
  const projectId = currentProjectId ?? '';

  const ticketingQuery = useQuery({
    queryKey: helpers.getTicketingQueryKey({ project_id: projectId }),
    queryFn:
      currentProjectId && isPrimaryUser
        ? async () => {
            const response =
              await api.ticketingIntegrations.hasCascadeTicketing({
                project_id: currentProjectId,
              });

            return response.has_cascade_ticketing;
          }
        : skipToken,
  });

  const canViewTickets = ticketingQuery.data === true;
  const { isLoading: isCheckingTicketing, loadError: ticketingError } =
    getQueryLoadState(ticketingQuery);

  const listRequest =
    currentProjectId && list && canViewTickets
      ? { ...list, project_id: currentProjectId }
      : undefined;

  const ticketsQuery = useQuery({
    queryKey: helpers.getTicketsQueryKey({ ...list, project_id: projectId }),
    queryFn: listRequest
      ? () => api.ticketing.listTickets(listRequest)
      : skipToken,
    placeholderData: (previousData, previousQuery) =>
      listRequest && previousQuery?.queryKey[2].project_id === projectId
        ? previousData
        : undefined,
  });

  const ticketQuery = useQuery({
    queryKey: helpers.getTicketQueryKey({
      display_id: displayId ?? '',
      project_id: projectId,
    }),
    queryFn:
      currentProjectId && displayId && canViewTickets
        ? () =>
            api.ticketing.getTicket({
              display_id: displayId,
              project_id: currentProjectId,
            })
        : skipToken,
  });

  const labelsQuery = useQuery({
    queryKey: helpers.getLabelsQueryKey({ ...labels, project_id: projectId }),
    queryFn:
      currentProjectId && labels
        ? async () => {
            const response = await api.ticketing.listProjectLabels({
              ...labels,
              project_id: currentProjectId,
            });

            return response.labels;
          }
        : skipToken,
  });

  const tickets = useMemo(
    () => ticketsQuery.data?.items ?? [],
    [ticketsQuery.data],
  );

  const totalTickets = ticketsQuery.data?.total_count;
  const ticketPageCount = ticketsQuery.data
    ? helpers.getTicketPageCount(ticketsQuery.data)
    : undefined;

  const projectLabels = useMemo(
    () => labelsQuery.data ?? [],
    [labelsQuery.data],
  );

  const { isLoading: isLoadingTickets, loadError: ticketsError } =
    getQueryLoadState(ticketsQuery);

  const { isLoading: isLoadingCurrentTicket, loadError: currentTicketError } =
    getQueryLoadState(ticketQuery);

  const { isLoading: isLoadingLabels, loadError: labelsError } =
    getQueryLoadState(labelsQuery);

  const currentTicket = ticketQuery.data?.ticket;
  const currentTicketMessages = ticketQuery.data?.ticket_messages;
  const currentTicketConversationId =
    helpers.getTicketConversationId(currentTicket);

  const hasUnreadMessages =
    user !== undefined &&
    helpers.hasUnreadFromOthers(currentTicketMessages, user.id);

  const { refetch: refetchTicketing } = ticketingQuery;
  const { refetch: refetchTickets } = ticketsQuery;

  const reloadTicketing = useCallback(async () => {
    await refetchTicketing();
  }, [refetchTicketing]);

  const reloadTickets = useCallback(async () => {
    await refetchTickets();
  }, [refetchTickets]);

  const { mutateAsync: updateTicket, isPending: isUpdatingTicket } =
    useMutation({
      mutationFn: ({ files, ...payload }: TTicketUpdate) => {
        const request = { ...payload, project_id: requireProjectId() };

        return helpers.hasAttachments(files)
          ? api.ticketing.updateTicketV2({ request, files })
          : api.ticketing.updateTicket(request);
      },
      onSuccess: () => invalidateQueries(...helpers.ticketQueryPrefixes),
    });

  const { mutate: markMessagesAsRead } = useProjectMutation(
    api.ticketing.setTicketingMessagesToRead,
    requireProjectId,
    helpers.ticketQueryPrefixes,
  );

  const { mutateAsync: createTicket, isPending: isCreatingTicket } =
    useMutation({
      mutationFn: ({ files, ...payload }: TTicketCreation) => {
        const request = { ...payload, project_id: requireProjectId() };

        return helpers.hasAttachments(files)
          ? api.ticketing.escalateConversationV2({ request, files })
          : api.ticketing.escalateConversation(request);
      },
      onSuccess: (response, { conversation_id: conversationId }) => {
        if (!response.success) {
          return;
        }

        onConversationEscalated?.(conversationId);

        return invalidateQueries(helpers.ticketsQueryPrefix);
      },
    });

  const getTicketingOptions = useCallback(
    (request: TTicketingOptionsRequest) =>
      api.ticketingIntegrations.getTicketingSystemOptions({
        ...request,
        project_id: requireProjectId(),
      }),
    [api, requireProjectId],
  );

  const { mutateAsync: createLabel } = useProjectMutation(
    api.ticketing.createProjectLabel,
    requireProjectId,
    [helpers.labelsQueryPrefix],
  );

  const { mutateAsync: updateLabel } = useProjectMutation(
    api.ticketing.updateProjectLabel,
    requireProjectId,
    [helpers.labelsQueryPrefix],
  );

  const { mutateAsync: deleteLabel } = useProjectMutation(
    api.ticketing.deleteProjectLabel,
    requireProjectId,
    [helpers.labelsQueryPrefix, ...helpers.ticketQueryPrefixes],
  );

  useEffect(() => {
    if (!displayId || !hasUnreadMessages) {
      return;
    }

    markMessagesAsRead({ display_id: displayId });
  }, [displayId, hasUnreadMessages, markMessagesAsRead]);

  const value = useMemo<TTicketsContextValue>(
    () => ({
      canViewTickets,
      isCheckingTicketing,
      ticketingError,
      tickets,
      totalTickets,
      ticketPageCount,
      isLoadingTickets,
      ticketsError,
      currentTicket,
      currentTicketMessages,
      currentTicketConversationId,
      isLoadingCurrentTicket,
      currentTicketError,
      labels: projectLabels,
      isLoadingLabels,
      labelsError,
      isUpdatingTicket,
      isCreatingTicket,
      reloadTicketing,
      reloadTickets,
      updateTicket,
      createTicket,
      getTicketingOptions,
      createLabel,
      updateLabel,
      deleteLabel,
    }),
    [
      canViewTickets,
      isCheckingTicketing,
      ticketingError,
      tickets,
      totalTickets,
      ticketPageCount,
      isLoadingTickets,
      ticketsError,
      currentTicket,
      currentTicketMessages,
      currentTicketConversationId,
      isLoadingCurrentTicket,
      currentTicketError,
      projectLabels,
      isLoadingLabels,
      labelsError,
      isUpdatingTicket,
      isCreatingTicket,
      reloadTicketing,
      reloadTickets,
      updateTicket,
      createTicket,
      getTicketingOptions,
      createLabel,
      updateLabel,
      deleteLabel,
    ],
  );

  return (
    <TicketsContext.Provider value={value}>{children}</TicketsContext.Provider>
  );
};
